import { DatePipe, DecimalPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { IShadowTableData } from 'src/app/interfaces/IShadowTableData';
import { ITableHeader } from 'src/app/interfaces/ITableHeader';
import { ElectionService } from 'src/app/services/election.service';

@Component({
  selector: 'distribution-of-preferences',
  templateUrl: './distribution-of-preferences.component.html',
  styleUrls: ['./distribution-of-preferences.component.scss']
})
export class DistributionOfPreferencesComponent implements AfterViewInit, OnDestroy {
  @Input() title = 'Distribution of Preferences';

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();


  public columns: ITableHeader[] = [];
  public shadowTable: IShadowTableData[][] = [];
  public dataSource: any = null;
  public displayedColumns: string[] = [];

  public majoritySubheading = '';
  public countConductedSubheading = '';
  public tableDistrictKey = '';

  constructor(
    public electionService: ElectionService,
    public datePipe: DatePipe,
    public decimalPipe: DecimalPipe,
    private cdr: ChangeDetectorRef
    ) { 
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
      if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
        this.loadTable();
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
      this.loadTable();
    });
  }
  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }


  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadTable();
    }
  }

  public customTrackBy(index: any, item: any) { return item.column; }

  trackByDistrictAndColumn(index: number, item: { column: string }): string {
    return (this.tableDistrictKey || '') + '_' + item.column;
  }

  private loadTable(): void {
    const page = this.electionService.selectedPage;
    if (page.pageType !== PageTypeEnum.CountHADistrict || !page.countDistrict) {
      this.tableDistrictKey = '';
      this.columns = [];
      this.shadowTable = [];
      this.displayedColumns = [];
      this.dataSource = new MatTableDataSource([]);
      this.cdr.markForCheck();
      return;
    }
    this.columns = [];
    this.shadowTable = [];
    this.displayedColumns = [];
    this.dataSource = new MatTableDataSource([]);
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    const foundDistrict = election?.electionCount?.getDistrict(page.countDistrict);
    if (foundDistrict) {
      this.tableDistrictKey = page.countDistrict ?? '';
      foundDistrict.candidates.sort((a, b) => a.candidateId - b.candidateId);

      let majority: number = 0;
      const tmpVotes: (number | null)[][] = [];
      /** Lightweight view model for column headers, decoupled from CandidateCount ids. */
      type DisplayCandidate = { candidateName: string; partyId: string; partyName: string; colourCode: string };
      /** When using finalDistribution, columns follow finalDistribution order (by candidateId 950,951,...); may differ from static candidateId order (1,2,3,...). */
      let candidatesForDisplay: DisplayCandidate[] = [];

      // Prefer finalDistribution from API (haChange) when present
      const finalDist = foundDistrict.finalDistribution;
      if (finalDist?.length) {
        // 1) Build tmpVotes in finalDistribution order (by index) so column i = candidateResults[i]
        for (let r = 0; r < finalDist.length; r++) {
          const round = finalDist[r];
          const row: (number | null)[] = round.candidateResults.map(cr =>
            cr.progressiveTotal != null ? +cr.progressiveTotal : null
          );
          tmpVotes.push(row);
        }

        // 2) Build display candidates aligned to candidateResults[i]
        const firstRoundResults = finalDist[0].candidateResults;
        const declarations = foundDistrict.declarations;
        const firstDeclWithVotes = declarations?.find(d => d.candidateVotes?.length);
        const declById = new Map<number, { name: string; partyName: string }>();
        if (firstDeclWithVotes?.candidateVotes?.length) {
          firstDeclWithVotes.candidateVotes.forEach(cv => {
            declById.set(cv.candidateId, {
              name: (cv.candidateFullName || '').trim(),
              partyName: (cv.partyName || '').trim()
            });
          });
        }
        const norm = (s: string) =>
          (s || '')
            .replace(/,/g, ' ')
            .split(/\s+/)
            .filter(Boolean)
            .sort()
            .join(' ')
            .toLowerCase();

        candidatesForDisplay = firstRoundResults.map(cr => {
          // Try match by candidateId directly (for elections where ids already match)
          const byId = foundDistrict.candidates.find(c => c.candidateId === cr.candidateId);

          // Try via declarations id -> full name, then match name back to static candidates
          const decl = declById.get(cr.candidateId);
          const byName =
            decl &&
            foundDistrict.candidates.find(c => norm(c.candidateName) === norm(decl.name));

          const source = byId ?? byName;

          return {
            candidateName: decl?.name || source?.candidateName || '',
            partyId: source?.partyId || '',
            partyName: source?.partyName || decl?.partyName || '',
            colourCode: source?.colourCode || ''
          };
        });
      } else {
        // Legacy path: use per-candidate distributionVotes array (one fewer rounds than candidates)
        for (let i = 0; i < foundDistrict.candidates.length - 1; i++) {
          const tmpRow: (number | null)[] = [];
          foundDistrict.candidates.forEach(candidate => {
            tmpRow.push(
              candidate.distributionVotes && i < candidate.distributionVotes.length
                ? candidate.distributionVotes[i]?.distributionVote ?? 0
                : null
            );
          });
          tmpVotes.push(tmpRow);
        }
        candidatesForDisplay = foundDistrict.candidates.map(c => ({
          candidateName: c.candidateName,
          partyId: c.partyId,
          partyName: c.partyName || '',
          colourCode: c.colourCode
        }));
      }

      if (tmpVotes.length > 0) {
        tmpVotes[0].forEach(item => {
          majority += item ?? 0;
        });
        majority = Math.floor(majority / 2) + 1;
      }
 
      // Show the HA last-updated timestamp (same logic as district header)
      const lastUpdate = this.electionService.getLastUpdate(page.electionDateString ?? '', true);
      const countDateStr = lastUpdate
        ? this.datePipe.transform(lastUpdate, 'd/M/yyyy')
        : null;

      this.majoritySubheading = `${this.decimalPipe.transform(majority, '')} votes required for an absolute majority`;
      this.countConductedSubheading = countDateStr ? `Count conducted on ${countDateStr}` : '';

      this.columns.push({
        column: '0',
        label: '',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 120,
        isBold: false,
        isRightJustify: false,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });

      candidatesForDisplay.forEach((candidate, index) => {
        this.columns.push({
          column: (index + 1).toString(),
          label: candidate.candidateName,
          labelTwo: candidate.partyId || '',
          isTwoRowLabel: true,
          minWidth: 120,
          isBold: false,
          isRightJustify: true,
          isPercent: false,
          isColour: false,
          isColourBar: false,
          colourCode: candidate.colourCode
        });
      });
      this.columns.push({
        column: (candidatesForDisplay.length + 1).toString(),
        label: 'Total',
        labelTwo: 'Votes',
        isTwoRowLabel: true,
        minWidth: 80,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });
     


      let tmpDistributionRow: IShadowTableData[] = [];
      let totalVotes = 0;
      // first row
      if (tmpVotes.length > 0) {
        tmpDistributionRow.push({
          isNumber: false,
          numberData: 0,
          stringData: 'First preference votes',
          isBold: false
        });
        tmpVotes[0].forEach(vote => {
          totalVotes += vote ? +vote : 0;
          tmpDistributionRow.push({
            isNumber: false,
            numberData: 0,
            stringData: this.decimalPipe.transform(vote, '')?.toString() ?? '',
            isBold: false
          });
        });
        tmpDistributionRow.push({
          isNumber: false,
          numberData: 0,
          stringData: this.decimalPipe.transform(totalVotes, '')?.toString() ?? '',
          isBold: false
        });
        this.shadowTable.push(tmpDistributionRow); 
        tmpDistributionRow = [];

        for (let round = 1; round < tmpVotes.length; round++) {
          totalVotes = 0;
          const excludedIndex = this.getExcludedIndex(round, tmpVotes);
          const excludedPreviously = this.getExcludedIndicesBeforeRound(tmpVotes, round);
          const excludedCandidateName = candidatesForDisplay[excludedIndex]?.candidateName ?? finalDist?.[round]?.excludedCandidateName?.trim() ?? '';
          tmpDistributionRow.push({
            isNumber: false,
            numberData: 0,
            stringData: this.excludeCandidateText(round, excludedCandidateName),
            isBold: false
          });

          // candidates: blank for already-excluded, else distribution value
          tmpVotes[round].forEach((vote, index) => {
            if (excludedPreviously.has(index)) {
              tmpDistributionRow.push({
                isNumber: false,
                numberData: 0,
                stringData: '',
                isBold: false
              });
            } else {
              const diff = this.decimalPipe.transform(Math.abs((tmpVotes[round][index] ?? 0) - (tmpVotes[round - 1][index] ?? 0) ), '');
              tmpDistributionRow.push({
                isNumber: false,
                numberData: 0,
                stringData: index === excludedIndex ? `- ${diff}` : diff !== '0' ? `+ ${diff}` : '0',
                isBold: false
              });
            }
          });

          // total
          tmpDistributionRow.push({
            isNumber: false,
            numberData: 0,
            stringData: '0',
            isBold: false
          });

          this.shadowTable.push(tmpDistributionRow); 
          tmpDistributionRow = [];


          tmpDistributionRow.push({
            isNumber: false,
            numberData: 0,
            stringData: round < tmpVotes.length - 1 ? 'Progressive Total' : 'Final District Totals',
            isBold: true
          });

          // candidates: blank for already-excluded, 'Excluded' for this round, else vote count
          tmpVotes[round].forEach((vote, index) => {
            totalVotes += vote ? +vote : 0;
            if (excludedPreviously.has(index)) {
              tmpDistributionRow.push({
                isNumber: false,
                numberData: 0,
                stringData: '',
                isBold: true
              });
            } else {
              tmpDistributionRow.push({
                isNumber: false,
                numberData: 0,
                stringData: index === excludedIndex ? 'Excluded' : this.decimalPipe.transform(vote, '') ?? '',
                isBold: true
              });
            }
          });

          // total
          tmpDistributionRow.push({
            isNumber: false,
            numberData: 0,
            stringData: this.decimalPipe.transform(totalVotes, '')?.toString() ?? '',
            isBold: true
          });

          this.shadowTable.push(tmpDistributionRow); 
          tmpDistributionRow = [];
        }


        // Final %
        tmpDistributionRow.push({
          isNumber: false,
          numberData: 0,
          stringData: 'Final %',
          isBold: true
        });

        // candidates   totalVotes
        const finalRound = tmpVotes.length - 1;
        tmpVotes[finalRound].forEach((vote, index) => {
          tmpDistributionRow.push({
            isNumber: false,   
            numberData:  0,
            stringData: vote && totalVotes > 0 ? (this.decimalPipe.transform(((vote ?? 0) / totalVotes) * 100, '1.1-1') ?? '') + ' %' : '', 
            isBold: true
          });
        });

        // total
        tmpDistributionRow.push({
          isNumber: false,
          numberData: 0,
          stringData: '100 %',
          isBold: true
        });
        this.shadowTable.push(tmpDistributionRow); 
        tmpDistributionRow = [];

        // Election Result
        tmpDistributionRow.push({
          isNumber: false,
          numberData: 0,
          stringData: 'Election Result',
          isBold: true
        });

        // candidates: use isElected from finalDistribution when available, else vote > majority
        const lastRoundResults = finalDist?.[finalRound]?.candidateResults;
        tmpVotes[finalRound].forEach((vote, index) => {
          const isElected = lastRoundResults?.[index]?.isElected ?? (!!(vote && vote > majority));
          tmpDistributionRow.push({
            isNumber: false,
            numberData: 0,
            stringData: isElected ? 'Elected' : '',
            isBold: true
          });
        });

        // total
        tmpDistributionRow.push({
          isNumber: false,
          numberData: 0,
          stringData: '',
          isBold: true
        });
        this.shadowTable.push(tmpDistributionRow); 
        tmpDistributionRow = [];

      }
       
     

    }



    const output = [];
    for (let i = 0; i < this.shadowTable.length; i++) {
      const newObj: {[k: string]: any} = {};
      for (let j = 0; j < this.columns.length; j++) {
        const tmpName: string = this.columns[j].column;
        const x = tmpName;
        const tmp = this.shadowTable[i][j].isNumber ? +this.shadowTable[i][j].numberData : this.shadowTable[i][j].stringData;
        newObj[tmpName] = tmp;
      }
      output.push(newObj);
    }

    this.displayedColumns = this.columns.map(c => c.column);
    if (this.shadowTable?.length > 0) {
      this.dataSource = new MatTableDataSource(output);
    }
    this.cdr.detectChanges();
  }


  public getExcludedIndex(round: number, tmpVotes: (number | null)[][]): number {
    let result = -1;
    for (let i = 0; i <= tmpVotes.length; i++) {
      if (tmpVotes[round - 1][i] !== null && (round === tmpVotes.length || tmpVotes[round][i] === null)) {
        result = i;
      }
    }
    return result;
  }

  /** Returns candidate indices excluded in rounds 1 through (round - 1). */
  private getExcludedIndicesBeforeRound(tmpVotes: (number | null)[][], round: number): Set<number> {
    const set = new Set<number>();
    for (let r = 1; r < round; r++) {
      const idx = this.getExcludedIndex(r, tmpVotes);
      if (idx >= 0) set.add(idx);
    }
    return set;
  }


  private excludeCandidateText(round: number, candidateName: string): string {
    const suffix = (n: number): string => {
      const mod100 = n % 100;
      if (mod100 >= 11 && mod100 <= 13) return 'th';
      switch (n % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };
    return `${round}${suffix(round)} exclusion. Distribution of ballot papers of ${candidateName}`;
  }

  public isBold(data: string): boolean {
    return data === 'Progressive Total' || data === 'Final District Totals' || data === 'Final %' || data == 'Election Result';
  }


  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
  
}
